import apiClient from '@/api/client'
import { graphqlQuery } from './graphql'
import type { FileUploadResponse, FileUploadCallback } from '@/types/survey'

// Types
export interface ClientProfile {
  id: string
  fullName: string
  displayName?: string
  phone?: string
  email?: string
  age?: number
  dateOfBirth?: string
  gender?: string
}

export interface Category {
  id: string
  nameEn: string
  nameAr: string
  descriptionEn?: string
  descriptionAr?: string
}

export interface ServiceTemplate {
  id: string
  name: string
  /** Client-facing display label; `name` is the internal one. */
  label?: string | null
  nameEn: string
  nameAr: string
  description?: string
  descriptionEn?: string
  descriptionAr?: string
  estimatedDuration?: number
  serviceIcon?: string
  isActive?: boolean
  effectiveDate?: string
  expiryDate?: string
  category?: {
    id: string
    name: string
    code: string
  }
  pricingStrategy?: {
    id: string
    basePrice: string
    currency: string
    description?: string
  }
}

export interface ServiceSpec {
  id: string
  orderIndex: number
  label: string
  serviceTemplate: ServiceTemplate
}

export interface AppointmentSpec {
  id: string
  orderIndex: number
  label: string
  labelAr?: string
  durationMinutes: number
  serviceSpecs: ServiceSpec[]
}

export interface OrderTemplate {
  id: string
  name: string
  description?: string
  /** English name (when backend provides localized fields) */
  nameEn?: string
  /** Arabic name */
  nameAr?: string
  /** English description */
  descriptionEn?: string
  /** Arabic description */
  descriptionAr?: string
  version: string
  isActive: boolean
  visibleAudiences?: Array<'b2c' | 'b2b'>
  structureDefinition?: {
    appointments?: Array<{
      appointment_template_id?: string
      template_id?: string
      order_index?: number
      label?: string
      duration_minutes?: number
      services?: Array<{
        service_template_id?: string
        order_index?: number
        label?: string
        duration_minutes?: number
      }>
    }>
  } | null
  maxAppointments: number
  ordersCount: number
  appointmentSpecsCount: number
  createdAt: string
  updatedAt: string
  appointmentSpecs?: AppointmentSpec[]
  /**
   * Advisory flag: false when the offering has no identifiable care recipient
   * (consultation, assessment, equipment-only), so beneficiary selection can be skipped.
   * Undefined on backends that don't expose the field yet; treat as required.
   */
  requiresBeneficiary?: boolean
}

export interface ServiceCategory {
  id: string
  code: string
  name: string
  nameEn?: string
  nameAr: string
  description?: string
  descriptionEn?: string
  descriptionAr?: string
  templatesCount: number
  isActive: boolean
  orderTemplates: OrderTemplate[]
}

export interface Beneficiary {
  id: string
  fullName: string
  firstName: string
  lastName: string
  gender?: string
  dateOfBirth?: string
  relationship?: string
}

// Create Beneficiary (POST /api/beneficiaries/create/)
export const BENEFICIARY_RELATIONSHIPS = [
  'self',
  'spouse',
  'child',
  'parent',
  'sibling',
  'grandparent',
  'grandchild',
  'other_family',
  'friend',
  'other',
] as const
export type BeneficiaryRelationship = (typeof BENEFICIARY_RELATIONSHIPS)[number]

export const BENEFICIARY_IDENTIFICATION_TYPES = [
  'iqama',
  'national_id',
  'passport',
  'visa',
  'other',
] as const
export type BeneficiaryIdentificationType =
  (typeof BENEFICIARY_IDENTIFICATION_TYPES)[number]

export interface CreateBeneficiaryRequest {
  client_id?: string // Omit for "my beneficiary"; include for admin/ops creating for another client
  /** When the ordering party is a business client profile (self-order / family under BC) */
  business_client_profile_id?: string
  first_name: string
  last_name?: string
  date_of_birth: string // YYYY-MM-DD
  relationship: BeneficiaryRelationship
  identification_number: string
  identification_type: BeneficiaryIdentificationType
  gender?: 'male' | 'female' | 'other' | 'prefer_not_to_say'
  phone_number?: string
  email?: string
  care_level?: string
  medical_conditions?: string[]
  care_preferences?: Record<string, unknown>
}

export interface CreateBeneficiaryResponse {
  success: boolean
  beneficiary: {
    id: string
    full_name: string
    date_of_birth: string
    gender?: string
    relationship_to_client: string
    care_level?: string
    medical_conditions?: string[]
    care_preferences?: Record<string, unknown>
    owner_id: string
    owner_name: string
  }
  message: string
}

export interface Order {
  id: string
  orderNumber: string
  status: string
  priority: string
  clientName?: string
  beneficiaryName?: string
  totalAmount?: string
  vatAmount?: string
  paymentStatus?: string
  hasPayments?: boolean
  orderTemplate?: {
    id: string
    name: string
  }
  pricingStrategy?: {
    id: string
    name: string
    basePrice: string
    currency: string
    taxRate?: string
  }
  appointments?: Array<{
    id: string
    status: string
    scheduledStart: string
    scheduledEnd: string
    location?: {
      city?: string
      streetAddress1?: string
    }
    services?: Array<{
      id: string
      status: string
      serviceTemplate?: {
        id: string
        name: string
      }
    }>
  }>
}

export interface PaymentStrategy {
  id: string
  name: string
  description?: string
  processorName: string
  strategyType: string
  isActive: boolean
  isDefault: boolean
  displayOrder: number
  iconName?: string
}

export interface SalesInvoice {
  id: string
  invoiceNumber: string
  totalAmount: string
  subtotal: string
  taxAmount: string
  currency: string
  status: string
  dueDate?: string
  createdAt: string
  quotation?: {
    id: string
    quotationNumber: string
    totalAmount: string
  }
  appointmentBreakdowns?: Array<{
    appointmentId: string
    scheduledStart: string
    subtotal: string
    taxAmount: string
    totalAmount: string
    currency: string
    servicesCount: number
  }>
}

// ============ Client Search Queries ============

/**
 * Search client by phone number
 * Returns a single client profile or null if not found
 */
export async function searchClientByPhone(phoneNumber: string): Promise<ClientProfile | null> {
  const query = `
    query GetClientProfileByPhone($phoneNumber: String!) {
      clientProfileByPhone(phoneNumber: $phoneNumber) {
        id
        fullName
        displayName
        age
        dateOfBirth
        gender
      }
    }
  `
  
  console.log('[searchClientByPhone] Searching for:', phoneNumber)
  
  const response = await graphqlQuery<{ clientProfileByPhone: ClientProfile | null }>(
    query,
    { phoneNumber }
  )
  
  console.log('[searchClientByPhone] Full Response:', JSON.stringify(response, null, 2))
  
  if (response.errors) {
    console.warn('[searchClientByPhone] GraphQL Errors:', response.errors)
    return null
  }
  
  const result = response.data?.clientProfileByPhone || null
  console.log('[searchClientByPhone] Result:', result)
  
  return result
}

/**
 * Search clients by name (partial match)
 * Returns an array of matching client profiles
 * NOTE: Uses clientProfilesByName (plural) for array results
 */
export async function searchClientByName(fullName: string): Promise<ClientProfile[]> {
  // Use plural query name for array results
  const query = `
    query GetClientProfilesByName($fullName: String!) {
      clientProfilesByName(fullName: $fullName) {
        id
        fullName
        displayName
        age
        dateOfBirth
        gender
      }
    }
  `
  
  console.log('[searchClientByName] Searching for:', fullName)
  console.log('[searchClientByName] Using plural query: clientProfilesByName')
  
  const response = await graphqlQuery<{ clientProfilesByName: ClientProfile[] }>(
    query,
    { fullName }
  )
  
  console.log('[searchClientByName] Full Response:', JSON.stringify(response, null, 2))
  
  if (response.errors) {
    console.warn('[searchClientByName] GraphQL Errors:', response.errors)
    return []
  }
  
  const results = response.data?.clientProfilesByName || []
  console.log('[searchClientByName] Results count:', results.length)
  console.log('[searchClientByName] Results:', results)
  
  return results
}

/**
 * Universal client search - automatically detects if searching by phone or name
 * Phone numbers are detected by checking if the input starts with digits
 */
export async function searchClients(searchTerm: string): Promise<ClientProfile[]> {
  console.log('[searchClients] ===================')
  console.log('[searchClients] Input searchTerm:', searchTerm)
  console.log('[searchClients] searchTerm length:', searchTerm?.length)
  
  if (!searchTerm || searchTerm.length < 2) {
    console.log('[searchClients] Search term too short, returning empty')
    return []
  }

  const trimmedTerm = searchTerm.trim()
  console.log('[searchClients] Trimmed term:', trimmedTerm)
  
  // Check if the search term looks like a phone number (starts with digits or +)
  const isPhoneSearch = /^[\d+]/.test(trimmedTerm)
  console.log('[searchClients] Is phone search?', isPhoneSearch)

  if (isPhoneSearch) {
    console.log('[searchClients] -> Using PHONE search')
    // Search by phone - returns single result or empty array
    const client = await searchClientByPhone(trimmedTerm)
    const results = client ? [client] : []
    console.log('[searchClients] Phone search results:', results)
    return results
  } else {
    console.log('[searchClients] -> Using NAME search')
    // Search by name - returns multiple results
    const results = await searchClientByName(trimmedTerm)
    console.log('[searchClients] Name search results:', results)
    return results
  }
}

// ============ Category Queries ============

export async function getCategories(): Promise<Category[]> {
  const query = `
    query GetCategories {
      categories {
        id
        nameEn
        nameAr
        descriptionEn
        descriptionAr
      }
    }
  `
  const response = await graphqlQuery<{ categories: Category[] }>(query)
  if (response.errors) {
    throw new Error(response.errors[0]?.message || 'Failed to fetch categories')
  }
  if (!response.data) {
    throw new Error('No data returned from GraphQL query')
  }
  return response.data.categories
}

export async function getCategoriesWithTemplates(
  businessAccountId?: string | null
): Promise<ServiceCategory[]> {
  // First try with full nested data
  const query = `
    query GetCategoriesWithTemplates($businessAccountId: ID) {
      serviceCategories(isActive: true) {
        id
        code
        name
        nameEn
        nameAr
        description
        descriptionEn
        descriptionAr
        templatesCount(businessAccountId: $businessAccountId)
        isActive
        orderTemplates(isActive: true, businessAccountId: $businessAccountId) {
          id
          name
          description
          nameEn
          nameAr
          descriptionEn
          descriptionAr
          version
          isActive
          visibleAudiences
          maxAppointments
          ordersCount
          appointmentSpecsCount
          createdAt
          updatedAt
        }
      }
    }
  `
  const response = await graphqlQuery<{ serviceCategories: ServiceCategory[] }>(
    query,
    { businessAccountId: businessAccountId || undefined }
  )
  
  // If there are errors but we still have data, try to use it
  // This handles cases where orderTemplates fails but categories succeed
  if (response.errors && response.errors.length > 0) {
    const errorMessage = response.errors[0]?.message || 'Failed to fetch categories with templates'
    
    // Check if we have partial data (categories but no templates)
    if (response.data?.serviceCategories && response.data.serviceCategories.length > 0) {
      console.warn('GraphQL partial error:', errorMessage)
      console.warn('Returning categories without templates. Error:', response.errors)
      
      // Return categories with empty templates array
      return response.data.serviceCategories.map(category => ({
        ...category,
        orderTemplates: category.orderTemplates || []
      }))
    }
    
    // If no data at all, throw error
    throw new Error(errorMessage)
  }
  
  if (!response.data) {
    throw new Error('No data returned from GraphQL query')
  }
  return response.data.serviceCategories
}

function appointmentSpecsFromStructureDefinition(
  template: OrderTemplate
): AppointmentSpec[] {
  const appointments = template.structureDefinition?.appointments
  if (!Array.isArray(appointments)) return []

  return appointments
    .map((appointment, index): AppointmentSpec | null => {
      const appointmentTemplateId =
        appointment.appointment_template_id || appointment.template_id
      if (!appointmentTemplateId) return null

      const orderIndex = appointment.order_index ?? index
      return {
        id: appointmentTemplateId,
        orderIndex,
        label:
          appointment.label ||
          `Appointment ${orderIndex + 1}`,
        durationMinutes: appointment.duration_minutes ?? 0,
        serviceSpecs: (appointment.services || []).map((service, serviceIndex) => ({
          id: service.service_template_id || `${appointmentTemplateId}:${serviceIndex}`,
          orderIndex: service.order_index ?? serviceIndex,
          label: service.label || '',
          serviceTemplate: {
            id: service.service_template_id || '',
            name: service.label || '',
            nameEn: service.label || '',
            nameAr: service.label || '',
          },
        })),
      }
    })
    .filter((spec): spec is AppointmentSpec => spec != null)
}

/**
 * Tri-state cache for whether the deployed backend schema exposes
 * `OrderTemplate.requiresBeneficiary`: null = not probed yet, true/false = known.
 *
 * Selecting an unknown field fails the entire GraphQL query, so we probe once per page
 * load and remember the answer. Once the backend field is deployed everywhere, this can be
 * dropped and the query can select the field unconditionally.
 */
let requiresBeneficiarySupported: boolean | null = null

const UNKNOWN_FIELD_PATTERNS = [
  /cannot query field/i,
  /unknown field/i,
  /has no field/i,
  /is not defined by type/i,
]

function isUnknownFieldError(error: unknown, fieldName: string): boolean {
  const messages: string[] = []
  if (error instanceof Error && error.message) {
    messages.push(error.message)
  }
  // Schema validation errors may come back with a non-2xx status, in which case the
  // GraphQL errors live on the Axios response rather than in the thrown Error's message.
  const responseErrors = (
    error as { response?: { data?: { errors?: Array<{ message?: string }> } } }
  )?.response?.data?.errors
  if (Array.isArray(responseErrors)) {
    messages.push(...responseErrors.map((e) => e?.message ?? ''))
  }
  return messages.some(
    (message) =>
      message.includes(fieldName) &&
      UNKNOWN_FIELD_PATTERNS.some((pattern) => pattern.test(message))
  )
}

export async function getOrderTemplate(templateId: string): Promise<OrderTemplate> {
  if (requiresBeneficiarySupported === false) {
    return fetchOrderTemplate(templateId, false)
  }
  try {
    const template = await fetchOrderTemplate(templateId, true)
    requiresBeneficiarySupported = true
    return template
  } catch (error) {
    if (!isUnknownFieldError(error, 'requiresBeneficiary')) {
      throw error
    }
    requiresBeneficiarySupported = false
    return fetchOrderTemplate(templateId, false)
  }
}

async function fetchOrderTemplate(
  templateId: string,
  includeRequiresBeneficiary: boolean
): Promise<OrderTemplate> {
  const query = `
    query GetOrderTemplateStructure($orderTemplateId: ID!) {
      orderTemplate(id: $orderTemplateId) {
        id
        name
        description
        nameEn
        nameAr
        descriptionEn
        descriptionAr
        version
        isActive
        ${includeRequiresBeneficiary ? 'requiresBeneficiary' : ''}
        appointmentSpecs {
          id
          orderIndex
          label
          durationMinutes
          serviceSpecs {
            id
            orderIndex
            label
            serviceTemplate {
              id
              name
              description
              descriptionEn
              descriptionAr
              nameAr
              nameEn
              pricingStrategy {
                basePrice
                currency
                description
                id
              }
            }
          }
        }
      }
    }
  `
  const response = await graphqlQuery<{ orderTemplate: OrderTemplate }>(
    query,
    { orderTemplateId: templateId }
  )
  if (response.errors) {
    throw new Error(response.errors[0]?.message || 'Failed to fetch order template')
  }
  if (!response.data) {
    throw new Error('No data returned from GraphQL query')
  }
  const template = response.data.orderTemplate
  return {
    ...template,
    appointmentSpecs: template.appointmentSpecs?.length
      ? template.appointmentSpecs
      : appointmentSpecsFromStructureDefinition(template),
  }
}

/**
 * Get client profile ID from user ID
 * This is needed because beneficiaries are linked to client profiles, not users
 * 
 * Tries multiple approaches:
 * 1. Query GraphQL to get client profile by user ID
 * 2. Try alternative GraphQL queries
 */
export async function getClientProfileByUserId(userId: string): Promise<string | null> {
  // Try GraphQL query - need to find the correct query structure
  // Based on error message, it suggests: clientProfile, clientProfileByName, etc.
  
  // Option 1: Try querying user's client profile through user query
  const query1 = `
    query GetUserClientProfile($userId: ID!) {
      user(id: $userId) {
        id
        clientProfile {
          id
        }
      }
    }
  `
  
  try {
    const response = await graphqlQuery<{ user: { id: string; clientProfile?: { id: string } | null } | null }>(
      query1,
      { userId }
    )
    
    if (!response.errors && response.data?.user?.clientProfile?.id) {
      console.log('Found client profile via user query:', response.data.user.clientProfile.id)
      return response.data.user.clientProfile.id
    }
    
    if (response.errors) {
      console.warn('GraphQL user query failed:', response.errors)
    }
  } catch (err1) {
    console.warn('GraphQL user query error:', err1)
  }
  
  // Option 2: Try direct clientProfile query with userId
  const query2 = `
    query GetClientProfileByUserId($userId: ID!) {
      clientProfile(userId: $userId) {
        id
      }
    }
  `
  
  try {
    const response = await graphqlQuery<{ clientProfile: { id: string } | null }>(
      query2,
      { userId }
    )
    
    if (!response.errors && response.data?.clientProfile?.id) {
      console.log('Found client profile via clientProfile(userId):', response.data.clientProfile.id)
      return response.data.clientProfile.id
    }
    
    if (response.errors) {
      console.warn('GraphQL clientProfile(userId) failed:', response.errors)
    }
  } catch (err2) {
    console.warn('GraphQL clientProfile(userId) error:', err2)
  }
  
  // Option 3: Try querying all client profiles and filter (not ideal but might work)
  // This would require knowing the structure, so skipping for now
  
  console.error('Could not find client profile ID for userId:', userId)
  return null
}

export async function getClientBeneficiaries(clientProfileId: string): Promise<Beneficiary[]> {
  // CRITICAL: This MUST be a client profile ID, NOT a user ID
  console.log('getClientBeneficiaries called with clientProfileId:', clientProfileId)
  
  const query = `
    query GetClientBeneficiaries($clientId: ID!) {
      clientBeneficiaries(clientId: $clientId) {
        id
        fullName
        firstName
        lastName
        gender
        dateOfBirth
        relationship
      }
    }
  `
  
  const response = await graphqlQuery<{ clientBeneficiaries: Beneficiary[] }>(
    query,
    { clientId: clientProfileId } // Using clientProfileId in variables
  )
  
  if (response.errors) {
    const errorMsg = response.errors[0]?.message || 'Failed to fetch beneficiaries'
    console.error('GraphQL error fetching beneficiaries:', {
      error: errorMsg,
      clientProfileId,
      errors: response.errors
    })
    throw new Error(errorMsg)
  }
  
  if (!response.data) {
    throw new Error('No data returned from GraphQL query')
  }
  return response.data.clientBeneficiaries || []
}

function pickBeneficiaryString(obj: Record<string, unknown>, ...keys: string[]): string {
  for (const k of keys) {
    const v = obj[k]
    if (v !== undefined && v !== null && String(v).trim() !== '') return String(v).trim()
  }
  return ''
}

/** Merge nested serializer objects (profile, etc.) so id/name fields resolve correctly */
function flattenBeneficiaryRaw(raw: unknown): Record<string, unknown> {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {}
  const o = { ...(raw as Record<string, unknown>) }
  const nestKeys = [
    'profile',
    'beneficiary_profile',
    'beneficiaryProfile',
    'beneficiary',
    'user_profile',
    'userProfile',
  ] as const
  for (const key of nestKeys) {
    const n = o[key]
    if (n && typeof n === 'object' && !Array.isArray(n)) {
      for (const [k, v] of Object.entries(n as Record<string, unknown>)) {
        if ((o[k] === undefined || o[k] === null || o[k] === '') && v !== undefined && v !== null) {
          o[k] = v
        }
      }
    }
  }
  return o
}

/** Normalize REST or GraphQL-like beneficiary rows to the UI Beneficiary type */
function normalizeBeneficiaryRow(raw: Record<string, unknown>): Beneficiary {
  const row = flattenBeneficiaryRaw(raw)
  const id = pickBeneficiaryString(
    row,
    'id',
    'profile_id',
    'beneficiary_profile_id',
    'beneficiary_id',
  )
  const firstName = pickBeneficiaryString(row, 'firstName', 'first_name')
  const lastName = pickBeneficiaryString(row, 'lastName', 'last_name')
  const fullFromFields = pickBeneficiaryString(row, 'fullName', 'full_name')
  const fullName =
    fullFromFields ||
    `${firstName}${firstName && lastName ? ' ' : ''}${lastName}`.trim() ||
    firstName ||
    lastName

  return {
    id,
    fullName,
    firstName: firstName || fullName.split(/\s+/)[0] || '',
    lastName: lastName || fullName.split(/\s+/).slice(1).join(' ') || '',
    gender: pickBeneficiaryString(row, 'gender') || undefined,
    dateOfBirth:
      pickBeneficiaryString(row, 'dateOfBirth', 'date_of_birth') || undefined,
    relationship:
      pickBeneficiaryString(row, 'relationship', 'relationship_to_client') ||
      undefined,
  }
}

function isRecordArrayOfObjects(arr: unknown[]): boolean {
  return (
    arr.length > 0 &&
    typeof arr[0] === 'object' &&
    arr[0] !== null &&
    !Array.isArray(arr[0])
  )
}

function parseBeneficiariesListPayload(payload: unknown, depth = 0): Beneficiary[] {
  if (depth > 6 || payload == null) return []

  if (Array.isArray(payload)) {
    if (!isRecordArrayOfObjects(payload)) return []
    return payload.map((row) =>
      normalizeBeneficiaryRow(row as Record<string, unknown>)
    )
  }

  if (typeof payload !== 'object') return []

  const obj = payload as Record<string, unknown>
  const preferredKeys = [
    'results',
    'beneficiaries',
    'data',
    'items',
    'profiles',
    'payload',
    'rows',
  ]
  for (const k of preferredKeys) {
    const v = obj[k]
    if (Array.isArray(v) && isRecordArrayOfObjects(v)) {
      return parseBeneficiariesListPayload(v, depth + 1)
    }
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      const inner = parseBeneficiariesListPayload(v, depth + 1)
      if (inner.length > 0) return inner
    }
  }

  for (const v of Object.values(obj)) {
    if (Array.isArray(v) && isRecordArrayOfObjects(v)) {
      return parseBeneficiariesListPayload(v, depth + 1)
    }
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      const inner = parseBeneficiariesListPayload(v, depth + 1)
      if (inner.length > 0) return inner
    }
  }

  return []
}

/**
 * Order-creation beneficiary list for a business client profile (REST).
 * GET /api/v1/orders/business-clients/{business_client_profile_id}/beneficiaries/
 * Same conceptual shape as client beneficiaries for the UI.
 */
export async function getBusinessClientBeneficiaries(
  businessClientProfileId: string
): Promise<Beneficiary[]> {
  const response = await apiClient.get<unknown>(
    `/api/v1/orders/business-clients/${encodeURIComponent(businessClientProfileId)}/beneficiaries/`
  )
  return parseBeneficiariesListPayload(response.data).filter((b) => b.id)
}

/**
 * Create a new beneficiary
 * POST /api/beneficiaries/create/
 * - Client mode: omit client_id (beneficiary for current user's client profile)
 * - Admin/Operations mode: send client_id (beneficiary for that client)
 */
export async function createBeneficiary(
  data: CreateBeneficiaryRequest
): Promise<CreateBeneficiaryResponse> {
  const body: Record<string, unknown> = {
    first_name: data.first_name,
    last_name: data.last_name ?? '',
    date_of_birth: data.date_of_birth,
    relationship: data.relationship,
    identification_number: data.identification_number,
    identification_type: data.identification_type,
  }
  if (data.client_id) body.client_id = data.client_id
  if (data.business_client_profile_id) {
    body.business_client_profile_id = data.business_client_profile_id
  }
  if (data.gender) body.gender = data.gender
  if (data.phone_number) body.phone_number = data.phone_number
  if (data.email) body.email = data.email
  if (data.care_level) body.care_level = data.care_level
  if (data.medical_conditions?.length) body.medical_conditions = data.medical_conditions
  if (data.care_preferences && Object.keys(data.care_preferences).length > 0) {
    body.care_preferences = data.care_preferences
  }
  const response = await apiClient.post<CreateBeneficiaryResponse>(
    '/api/beneficiaries/create/',
    body,
    { timeout: 15000 }
  )
  return response.data
}

export async function getOrderDetails(orderId: string): Promise<Order> {
  const query = `
    query GetOrderDetails($id: ID!) {
      order(id: $id) {
        id
        orderNumber
        status
        priority
        clientName
        beneficiaryName
        totalAmount
        vatAmount
        paymentStatus
        hasPayments
        orderTemplate {
          id
          name
        }
        pricingStrategy {
          id
          name
          basePrice
          currency
          taxRate
        }
        appointments {
          id
          status
          scheduledStart
          scheduledEnd
          location {
            city
            streetAddress1
          }
          services {
            id
            status
            serviceTemplate {
              id
              name
            }
          }
        }
      }
    }
  `
  const response = await graphqlQuery<{ order: Order }>(
    query,
    { id: orderId }
  )
  if (response.errors) {
    throw new Error(response.errors[0]?.message || 'Failed to fetch order details')
  }
  if (!response.data) {
    throw new Error('No data returned from GraphQL query')
  }
  return response.data.order
}

export async function getPaymentStrategies(): Promise<PaymentStrategy[]> {
  const query = `
    query GetPaymentStrategies {
      acceptanceStrategies(isActive: true) {
        id
        name
        description
        processorName
        strategyType
        isActive
        isDefault
        displayOrder
        iconName
      }
    }
  `
  const response = await graphqlQuery<{ acceptanceStrategies: PaymentStrategy[] }>(query)
  if (response.errors) {
    throw new Error(response.errors[0]?.message || 'Failed to fetch payment strategies')
  }
  if (!response.data) {
    throw new Error('No data returned from GraphQL query')
  }
  return response.data.acceptanceStrategies
}

export async function getOrderInvoice(orderId: string): Promise<SalesInvoice | null> {
  const query = `
    query GetOrderInvoice($orderId: ID!) {
      salesInvoices(orderId: $orderId) {
        edges {
          node {
            id
            invoiceNumber
            totalAmount
            subtotal
            taxAmount
            currency
            status
            dueDate
            createdAt
            quotation {
              id
              quotationNumber
              totalAmount
            }
            appointmentBreakdowns {
              appointmentId
              scheduledStart
              subtotal
              taxAmount
              totalAmount
              currency
              servicesCount
            }
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  `
  const response = await graphqlQuery<{
    salesInvoices: {
      edges: Array<{ node: SalesInvoice }>
      pageInfo: {
        hasNextPage: boolean
        endCursor: string | null
      }
    }
  }>(
    query,
    { orderId }
  )
  if (response.errors) {
    throw new Error(response.errors[0]?.message || 'Failed to fetch invoice')
  }
  if (!response.data) {
    throw new Error('No data returned from GraphQL query')
  }
  const invoices = response.data.salesInvoices.edges.map((edge) => edge.node)
  return invoices[0] || null
}

export async function getOrderAggregatedSurvey(orderId: string): Promise<{
  currentAggregatedSurveySnapshotId: string | null
  aggregatedSurveyRevision: number
}> {
  const query = `
    query GetOrderAggregatedSurvey($orderId: ID!) {
      order(id: $orderId) {
        id
        status
        currentAggregatedSurveySnapshotId
        aggregatedSurveyRevision
      }
    }
  `
  const response = await graphqlQuery<{ order: { currentAggregatedSurveySnapshotId: string | null; aggregatedSurveyRevision: number } }>(
    query,
    { orderId }
  )
  if (response.errors) {
    throw new Error(response.errors[0]?.message || 'Failed to fetch survey snapshot ID')
  }
  if (!response.data) {
    throw new Error('No data returned from GraphQL query')
  }
  return {
    currentAggregatedSurveySnapshotId: response.data.order.currentAggregatedSurveySnapshotId,
    aggregatedSurveyRevision: response.data.order.aggregatedSurveyRevision,
  }
}

// REST API Calls
export interface CreateOrderFromTemplateRequest {
  template_id: string
  /** Set for entity flow (order for a client profile) */
  client_profile_id?: string
  /** Set for business client self-order */
  business_client_profile_id?: string
  scheduled_start: string
  appointments_schedule: Array<{
    appointment_template_id: string
    scheduled_start: string
  }>
  priority?: string
}

export interface CreateOrderResponse {
  order: {
    id: string
    order_number: string
    status: string
    priority: string
    order_template: string
    client_profile: string
    beneficiary_profile: string | null
    created_at: string
    appointments?: Array<{
      id: string
      status: string
      scheduled_start: string
      scheduled_end: string
    }>
  }
  message: string
  // Appointments might also be at root level in some responses
  appointments?: Array<{
    id: string
    status: string
    scheduled_start: string
    scheduled_end: string
  }>
}

export async function createOrderFromTemplate(
  data: CreateOrderFromTemplateRequest
): Promise<CreateOrderResponse> {
  const body: Record<string, unknown> = {
    template_id: data.template_id,
    scheduled_start: data.scheduled_start,
    appointments_schedule: data.appointments_schedule,
  }
  if (data.priority) body.priority = data.priority
  if (data.client_profile_id) body.client_profile_id = data.client_profile_id
  if (data.business_client_profile_id) {
    body.business_client_profile_id = data.business_client_profile_id
  }
  const response = await apiClient.post<CreateOrderResponse>(
    '/api/v1/orders/from-template/',
    body
  )
  return response.data
}

export async function submitOrder(orderId: string): Promise<{ order: { id: string; order_number: string; status: string }; message: string }> {
  const response = await apiClient.post(
    `/api/v1/orders/${orderId}/submit/`
  )
  return response.data
}

export async function updateOrderWithBeneficiary(
  orderId: string,
  beneficiaryProfileId: string
): Promise<{ order: { id: string; beneficiary_profile: string; status: string } }> {
  const response = await apiClient.patch(
    `/api/v1/orders/${orderId}/`,
    { beneficiary_profile_id: beneficiaryProfileId }
  )
  return response.data
}

export async function getEvaluationSnapshot(orderId: string): Promise<any> {
  const response = await apiClient.get(
    `/api/v1/evaluations/snapshots/?order_id=${orderId}`
  )
  return response.data.results[0] || null
}

export interface EvaluationResponse {
  id: string
  aggregated_survey_snapshot: string
  object_id: string
  content_type: number
  answers: Record<string, any>
  submission_status: string
  submitted: boolean
  created_at: string
}

export async function createEvaluationResponse(data: {
  aggregated_survey_snapshot: string
  object_id: string
  content_type: string
  answers: Record<string, any>
}): Promise<EvaluationResponse> {
  const response = await apiClient.post<EvaluationResponse>(
    '/api/v1/evaluations/responses/',
    data
  )
  return response.data
}

export async function updateEvaluationResponse(
  responseId: string,
  answers: Record<string, any>
): Promise<EvaluationResponse> {
  const response = await apiClient.patch<EvaluationResponse>(
    `/api/v1/evaluations/responses/${responseId}/`,
    { answers }
  )
  return response.data
}

export async function submitEvaluationResponse(
  responseId: string,
  answers: Record<string, any>
): Promise<{
  status: string
  submission_type: string
  submitted_at: string
  completion_percentage: number
  answered_questions: number
  total_questions: number
  is_fully_answered: boolean
  deaggregation_applied: boolean
  evaluation_triggered: boolean
  message: string
}> {
  const response = await apiClient.post(
    `/api/v1/evaluations/responses/${responseId}/submit/`,
    { answers }
  )
  return response.data
}

/**
 * Upload a file for an evaluation response question.
 * Uses the dedicated evaluation upload endpoint which creates a SurveyFileAnswer
 * linked to the response and triggers AI processing.
 */
export async function uploadEvaluationFile(
  responseId: string,
  file: File,
  questionCode: string,
  onProgress?: FileUploadCallback
): Promise<FileUploadResponse> {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('question_code', questionCode)

  const response = await apiClient.post<{
    id: string
    file_url: string
    original_filename: string
    file_size: number
    ai_processing_status: string
  }>(
    `/api/v1/evaluations/responses/${responseId}/upload-file/`,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const percentage = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          )
          onProgress({
            loaded: progressEvent.loaded,
            total: progressEvent.total,
            percentage,
          })
        }
      },
    }
  )

  const data = response.data
  return {
    file_id: data.id,
    file_type: questionCode,
    filename: data.original_filename,
    size: data.file_size,
    uploaded_at: new Date().toISOString(),
    url: data.file_url,
  }
}

export interface CreatePaymentTransactionRequest {
  sales_invoice_id: string
  acceptance_strategy_id: string
}

export interface PaymentTransactionResponse {
  transaction: {
    id: string
    transaction_number: string
    amount: string
    currency: string
    status: string
  }
  payment_result: {
    success: boolean
    payment_url?: string
    requires_redirect: boolean
    message: string | null
    error_message: string | null
  }
}

export async function createPaymentTransaction(
  data: CreatePaymentTransactionRequest
): Promise<PaymentTransactionResponse> {
  const response = await apiClient.post<PaymentTransactionResponse>(
    '/api/v1/finance/transactions/create/',
    data
  )
  return response.data
}

export interface ConfirmBankTransferRequest {
  transaction_id: string
  reference_number: string
  notes?: string
  attachment: File
}

export interface ConfirmBankTransferResponse {
  success: boolean
  reference_number: string
  transaction: {
    id: string
    transaction_number: string
    status: string
    amount: string
  }
  invoice: {
    id: string
    invoice_number: string
    status: string
    authorized: boolean
  }
  attachment_url?: string
}

export async function confirmBankTransferPayment(
  data: ConfirmBankTransferRequest
): Promise<ConfirmBankTransferResponse> {
  const formData = new FormData()
  formData.append('transaction_id', data.transaction_id)
  formData.append('reference_number', data.reference_number)
  if (data.notes) {
    formData.append('notes', data.notes)
  }
  formData.append('attachment', data.attachment)

  const response = await apiClient.post<ConfirmBankTransferResponse>(
    '/api/v1/finance/transactions/confirm-bank-transfer/',
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  )
  return response.data
}

export interface ConfirmCashPaymentRequest {
  transaction_id: string
  notes?: string
}

export interface ConfirmCashPaymentResponse {
  success: boolean
  collected_by: string
  transaction: {
    id: string
    transaction_number: string
    status: string
    amount: string
  }
  invoice: {
    id: string
    invoice_number: string
    status: string
    authorized: boolean
  }
}

export async function confirmCashPayment(
  data: ConfirmCashPaymentRequest
): Promise<ConfirmCashPaymentResponse> {
  const response = await apiClient.post<ConfirmCashPaymentResponse>(
    '/api/v1/finance/transactions/confirm-cash/',
    data
  )
  return response.data
}

export async function simulatePayment(transactionId: string): Promise<{
  success: boolean
  message: string
  transaction: {
    id: string
    transaction_number: string
    status: string
    amount: string
    currency: string
  }
  receipt: {
    id: string
    receipt_number: string
  }
}> {
  const response = await apiClient.post(
    '/api/v1/finance/transactions/simulate-payment/',
    { transaction_id: transactionId }
  )
  return response.data
}

// Service Templates
export interface GetServiceTemplatesOptions {
  /** Backend also applies the effective/expiry window when true. */
  isActive?: boolean
  limit?: number
}

export async function getServiceTemplates(
  options: GetServiceTemplatesOptions = {}
): Promise<ServiceTemplate[]> {
  const query = `
    query GetServiceTemplates($isActive: Boolean, $limit: Int) {
      serviceTemplates(isActive: $isActive, limit: $limit) {
        id
        name
        label
        nameEn
        nameAr
        description
        estimatedDuration
        serviceIcon
        isActive
        effectiveDate
        expiryDate
        category {
          id
          name
          code
        }
        pricingStrategy {
          id
          basePrice
          currency
          description
        }
      }
    }
  `
  const response = await graphqlQuery<{ serviceTemplates: ServiceTemplate[] }>(
    query,
    { isActive: options.isActive, limit: options.limit }
  )
  if (response.errors) {
    throw new Error(response.errors[0]?.message || 'Failed to fetch service templates')
  }
  if (!response.data) {
    throw new Error('No data returned from GraphQL query')
  }
  return response.data.serviceTemplates || []
}

// Add Service to Appointment
export interface AddServiceToAppointmentRequest {
  service_template_id: string
  client_request_id?: string
  notes?: string
}

export interface AddServiceToAppointmentResponse {
  service: {
    id: string
    status: string
    service_template_id: string
    appointment_id: string
    order_id: string
    notes?: string
    created_at: string
    updated_at: string
  }
  message: string
}

export async function addServiceToAppointment(
  appointmentId: string,
  data: AddServiceToAppointmentRequest
): Promise<AddServiceToAppointmentResponse> {
  const response = await apiClient.post<AddServiceToAppointmentResponse>(
    `/api/v1/orders/appointments/${appointmentId}/services/`,
    data
  )
  return response.data
}
